import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useToast } from "@chakra-ui/react";
import {
  FiBookOpen,
  FiExternalLink,
  FiFileText,
  FiHelpCircle,
  FiMessageCircle,
  FiRefreshCw,
  FiSave,
} from "react-icons/fi";

const DEFINITIONS = {
  "how-to-earn-tips": {
    title: "How to Earn Tips",
    description: "Manage the text displayed when a user opens How to Earn Tips from More Options.",
    icon: FiBookOpen,
    fields: ["title", "content"],
  },
  "community-guidelines": {
    title: "Community Guidelines",
    description: "Manage the community rules shown to users from More Options.",
    icon: FiFileText,
    fields: ["title", "content"],
  },
  "help-center": {
    title: "Help Center",
    description: "Set the support information users see when they open Help Center.",
    icon: FiHelpCircle,
    fields: ["title", "content", "email", "phone"],
  },
  community: {
    title: "Join Community",
    description: "Set one WhatsApp invite link for each user language. Users receive the link matching their selected language.",
    icon: FiMessageCircle,
    fields: ["whatsappCommunityGroups"],
  },
};

const emptyForm = () => ({
  title: "",
  content: "",
  email: "",
  phone: "",
  whatsappCommunityGroups: [],
  isActive: true,
});

const toFormData = (configuration) => ({
  ...emptyForm(),
  ...(configuration || {}),
  isActive: configuration?.isActive ?? true,
});

const getErrorMessage = (error) => {
  const payload = error?.response?.data;
  if (Array.isArray(payload?.errors) && payload.errors.length) {
    return payload.errors.map((item) => item.message).join(" ");
  }
  return payload?.message || "Unable to save the configuration. Please try again.";
};

const validateForm = (configurationType, formData) => {
  const definition = DEFINITIONS[configurationType];

  if (definition.fields.includes("title") && !formData.title.trim()) {
    return "Title is required";
  }
  if (definition.fields.includes("content") && !formData.content.trim()) {
    return "Description is required";
  }
  if (configurationType === "help-center") {
    if (!formData.email.trim()) return "Email address is required";
    if (!/^\S+@\S+\.\S+$/.test(formData.email.trim())) {
      return "Please enter a valid email address";
    }

    const phoneDigits = formData.phone.replace(/\D/g, "");
    if (!formData.phone.trim()) return "Phone number is required";
    if (!/^\+?[\d\s().-]+$/.test(formData.phone) || phoneDigits.length < 7 || phoneDigits.length > 15) {
      return "Please enter a valid phone number";
    }
  }
  if (configurationType === "community") {
    const languages = new Set();
    for (const group of formData.whatsappCommunityGroups) {
      const language = group.language.trim();
      if (!language) return "Language is required for every community";
      const languageKey = language.toLocaleLowerCase();
      if (languages.has(languageKey)) return "Each language can have only one WhatsApp community link";
      languages.add(languageKey);
      try {
        const url = new URL(group.link.trim());
        if (url.protocol !== "https:" || url.hostname.toLowerCase() !== "chat.whatsapp.com" || url.pathname.length <= 1) {
          return "Please enter a valid WhatsApp invite link";
        }
      } catch {
        return "Please enter a valid HTTPS URL";
      }
    }
  }
  return "";
};

const MoreOptionsConfiguration = () => {
  const [configurationType, setConfigurationType] = useState("how-to-earn-tips");
  const definition = DEFINITIONS[configurationType];
  const Icon = definition.icon;
  const toast = useToast();
  const [formData, setFormData] = useState(emptyForm);
  const [savedConfiguration, setSavedConfiguration] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [availableLanguages, setAvailableLanguages] = useState([]);

  const endpoint = useMemo(
    () => `/api/v1/admin/more-options/${configurationType}`,
    [configurationType]
  );

  const fetchConfiguration = useCallback(async () => {
    setLoading(true);
    setLoadError("");
    try {
      const response = await axios.get(endpoint);
      const configuration = response.data?.data || null;
      setSavedConfiguration(configuration);
      setFormData(toFormData(configuration));
    } catch (error) {
      setLoadError(error?.response?.data?.message || "Unable to load this configuration.");
      setSavedConfiguration(null);
      setFormData(emptyForm());
    } finally {
      setLoading(false);
    }
  }, [endpoint]);

  useEffect(() => {
    fetchConfiguration();
  }, [fetchConfiguration]);

  useEffect(() => {
    const fetchAvailableLanguages = async () => {
      try {
        const response = await axios.get("/api/v1/admin/more-options/community/languages");
        setAvailableLanguages(response.data?.data || []);
      } catch (error) {
        console.error("Unable to load available user languages:", error);
      }
    };
    fetchAvailableLanguages();
  }, []);

  const handleChange = (event) => {
    const { name, value, checked, type } = event.target;
    setFormData((current) => ({
      ...current,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const updateCommunityGroup = (index, field, value) => {
    setFormData((current) => ({
      ...current,
      whatsappCommunityGroups: current.whatsappCommunityGroups.map((group, groupIndex) =>
        groupIndex === index ? { ...group, [field]: value } : group
      ),
    }));
  };

  const addCommunityGroup = () => {
    setFormData((current) => ({
      ...current,
      whatsappCommunityGroups: [...current.whatsappCommunityGroups, { language: "", link: "" }],
    }));
  };

  const removeCommunityGroup = (index) => {
    const nextFormData = {
      ...formData,
      whatsappCommunityGroups: formData.whatsappCommunityGroups.filter((_, groupIndex) => groupIndex !== index),
    };

    setFormData(nextFormData);
    setSaving(true);
    axios.put(endpoint, nextFormData)
      .then((response) => {
        const configuration = response.data?.data || null;
        setSavedConfiguration(configuration);
        setFormData(toFormData(configuration));
        toast({ title: "Community removed successfully", status: "success", duration: 3500, isClosable: true });
      })
      .catch((error) => {
        setFormData(toFormData(savedConfiguration));
        toast({ title: getErrorMessage(error), status: "error", duration: 4500, isClosable: true });
      })
      .finally(() => setSaving(false));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const validationMessage = validateForm(configurationType, formData);
    if (validationMessage) {
      toast({ title: validationMessage, status: "error", duration: 3500, isClosable: true });
      return;
    }

    setSaving(true);
    try {
      const response = await axios.put(endpoint, formData);
      const configuration = response.data?.data || null;
      setSavedConfiguration(configuration);
      setFormData(toFormData(configuration));
      toast({
        title: response.data?.message || "Configuration saved successfully",
        status: "success",
        duration: 3500,
        isClosable: true,
      });
    } catch (error) {
      toast({ title: getErrorMessage(error), status: "error", duration: 4500, isClosable: true });
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setFormData(toFormData(savedConfiguration));
  };

  if (loading) {
    return (
      <div className="min-h-[360px] flex items-center justify-center">
        <div className="flex items-center gap-3 text-violet-700 font-medium">
          <FiRefreshCw className="animate-spin text-xl" /> Loading configuration...
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto">
      <div className="mb-8">
        <h1 className="text-3xl font-extrabold text-[var(--primary-solid)]">More Options</h1>
        <p className="text-[var(--black-text)]/70 mt-2">Manage the information available to users from the app's More Options menu.</p>
      </div>

      <div className="mb-8 grid grid-cols-1 sm:grid-cols-2 gap-3" role="tablist" aria-label="More Options settings">
        {Object.entries(DEFINITIONS).map(([type, option]) => {
          const OptionIcon = option.icon;
          const selected = type === configurationType;
          return (
            <button
              key={type}
              type="button"
              role="tab"
              aria-selected={selected}
              onClick={() => setConfigurationType(type)}
              className={`flex items-center gap-3 rounded-xl border-2 px-4 py-3 text-left transition-colors ${
                selected
                  ? "border-[var(--yellow)] bg-yellow-50 text-[var(--primary-solid)]"
                  : "border-gray-200 bg-white text-gray-700 hover:border-yellow-300 hover:bg-yellow-50/50"
              }`}
            >
              <OptionIcon className="text-xl shrink-0" />
              <span className="font-semibold">{option.title}</span>
            </button>
          );
        })}
      </div>

      <div className="mb-6">
        <h2 className="text-2xl font-bold text-[var(--primary-solid)] flex items-center gap-3">
          <Icon className="text-2xl" />
          {definition.title}
        </h2>
        <p className="text-[var(--black-text)]/70 mt-2">{definition.description}</p>
      </div>

      {loadError ? (
        <div className="mb-6 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-red-700 flex items-center justify-between gap-4">
          <span>{loadError}</span>
          <button type="button" onClick={fetchConfiguration} className="font-semibold underline">
            Retry
          </button>
        </div>
      ) : null}

      {!savedConfiguration ? (
        <div className="mb-6 rounded-xl border border-blue-200 bg-blue-50 px-4 py-3 text-blue-800">
          No configuration exists yet. Complete the form below and save it to make this option available in the app.
        </div>
      ) : null}

      <form onSubmit={handleSubmit} className="bg-white rounded-2xl border-2 border-gray-200 shadow-sm overflow-hidden">
        <div className="p-6 sm:p-8 space-y-6">
          {definition.fields.includes("title") ? (
            <FormField label="Title" required>
              <input
                name="title"
                value={formData.title}
                onChange={handleChange}
                maxLength={160}
                placeholder={definition.title}
                className="form-input pl-2 w-full"
              />
            </FormField>
          ) : null}

          {definition.fields.includes("content") ? (
            <FormField label={configurationType === "help-center" ? "Description" : "Content / Description"} required>
              <textarea
                name="content"
                value={formData.content}
                onChange={handleChange}
                maxLength={10000}
                rows={10}
                placeholder="Enter the text users should see in the app"
                className="form-input pl-2 w-full resize-y"
              />
              <p className="mt-1 text-xs text-gray-500">{formData.content.length}/10,000 characters</p>
            </FormField>
          ) : null}

          {definition.fields.includes("email") ? (
            <FormField label="Gmail / Email Address" required>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                maxLength={254}
                placeholder="support@example.com"
                className="form-input pl-2 w-full"
              />
            </FormField>
          ) : null}

          {definition.fields.includes("phone") ? (
            <FormField label="Phone Number" required hint="International numbers are supported, for example +91 98765 43210.">
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                maxLength={30}
                placeholder="+91 98765 43210"
                className="form-input pl-2 w-full"
              />
            </FormField>
          ) : null}

          {definition.fields.includes("whatsappCommunityGroups") ? (
            <FormField label="Language-specific WhatsApp Communities" required hint="Add one invite link per language. The language spelling must match the one selected by users, for example Hindi or English.">
              <div className="space-y-3">
                {formData.whatsappCommunityGroups.map((group, index) => (
                  <div key={index} className="grid grid-cols-1 gap-3 rounded-xl border border-gray-200 p-4 sm:grid-cols-[minmax(0,0.7fr)_minmax(0,1.5fr)_auto] sm:items-center">
                    <select
                      value={group.language}
                      onChange={(event) => updateCommunityGroup(index, "language", event.target.value)}
                      className="form-input pl-2 w-full"
                    >
                      <option value="">Select a user language</option>
                      {[...new Set([...availableLanguages, group.language].filter(Boolean))].map((language) => (
                        <option key={language} value={language}>{language}</option>
                      ))}
                    </select>
                    <div className="relative">
                      <FiExternalLink className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                      <input
                        type="url"
                        value={group.link}
                        onChange={(event) => updateCommunityGroup(index, "link", event.target.value)}
                        maxLength={2048}
                        placeholder="https://chat.whatsapp.com/..."
                        className="form-input  w-full pl-10"
                      />
                    </div>
                    <button type="button" onClick={() => removeCommunityGroup(index)} disabled={saving} className="rounded-lg border border-red-200 px-3 py-2 text-sm font-semibold text-red-600 hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-50">
                      Remove
                    </button>
                  </div>
                ))}
                <button type="button" onClick={addCommunityGroup} className="rounded-lg border-2 border-dashed border-violet-300 px-4 py-2.5 font-semibold text-violet-700 hover:bg-violet-50">
                  + Add language community
                </button>
              </div>
            </FormField>
          ) : null}

          <label className="flex items-start gap-3 rounded-xl border border-gray-200 bg-gray-50 p-4 cursor-pointer">
            <input
              type="checkbox"
              name="isActive"
              checked={formData.isActive}
              onChange={handleChange}
              className="mt-1 pl-2 h-4 w-4 accent-pink-600"
            />
            <span>
              <span className="block font-semibold text-[var(--black-text)]">Active</span>
              <span className="block text-sm text-gray-600 mt-0.5">
                When inactive, the mobile API returns an unavailable response and does not expose this configuration.
              </span>
            </span>
          </label>
        </div>

        <div className="border-t border-gray-200 bg-gray-50 px-6 sm:px-8 py-4 flex flex-col-reverse sm:flex-row sm:items-center sm:justify-between gap-3">
          <p className="text-xs text-gray-500">
            {savedConfiguration?.updatedAt
              ? `Last saved ${new Date(savedConfiguration.updatedAt).toLocaleString()}`
              : "Changes are published immediately after saving."}
          </p>
          <div className="flex gap-3">
            <button
              type="button"
              onClick={handleCancel}
              disabled={saving}
              className="px-5 py-2.5 rounded-lg border-2 border-gray-300 text-gray-700 font-semibold hover:bg-red-600 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-5 py-2.5 rounded-lg bg-[var(--yellow)] text-white font-semibold hover:opacity-90 disabled:opacity-50 flex items-center gap-2"
            >
              {saving ? <FiRefreshCw className="animate-spin" /> : <FiSave />}
              {saving ? "Saving..." : savedConfiguration ? "Update" : "Save"}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};

const FormField = ({ label, required, hint, children }) => (
  <div>
    <label className="block text-sm font-semibold text-[var(--black-text)] mb-2">
      {label} {required ? <span className="text-red-600">*</span> : null}
    </label>
    <div className="ml-2 sm:ml-3">{children}</div>
    {hint ? <p className="mt-1 text-xs text-gray-500">{hint}</p> : null}
  </div>
);

export default MoreOptionsConfiguration;
